import { Loader2, Wallet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';

import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { setCartCount } from '../../properties/property-view/slice/OrderCartSlice';
import { clearOrderData } from '../order-confirmation/order-confirmation-store/orderSlice';
import { updatesKeyOfBulkOrderCartData } from '../cart/utils/cartTransforms';
import { ROUTES } from '../../../config/routes';

import SavedCardsList from './components/SavedCardsList';
import PaymentOrderSummary from './components/PaymentOrderSummary';
import CardFormModal from './components/CardFormModal';
import DeleteCardModal from './components/DeleteCardModal';
import { MaxCardModal } from './components/MaxCardModal';
import { usePayment } from './hooks/usePayment';
import { useBulkImportBack } from './hooks/useBulkImportBack';
import { buildMapsFromLocation } from './utils/paymentUtils';
import { buildOrderPayment } from '@/util/orderPayment';
import type { PaymentPageProps } from './payment.types';

// Re-export cardTypes for backward compatibility with other modules
export { CardType as cardTypes } from '../../../types/order.types';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PaymentPage = (props: PaymentPageProps) => {
  const {
    setActiveStep,
    isBulkOrder = false,
    isSingleOrder: isSingleOrderProp = false,
    setPaymentInfo,
    setPromoCodeFromPayment,
    setPromoCodeFromPaymentBulkOrder,
    setSavingsAmountFromPayment,
    setCallSubmitOrders,
    failedCountInfo,
    fileNameInfo,
    orderCount,
    successData,
    fileIdInfo,
    paymentLoader,
    finalPaymentAmount,
    onSubmitSingleOrder,
  } = props;

  const payment = usePayment(props);
  const bulkBack = useBulkImportBack({
    fileIdInfo,
    setActiveStep: props.setActiveStep,
    setFailedData: props.setFailedData,
    setSuccessData: props.setSuccessData,
    setFixErrorTableData: props.setFixErrorTableData,
    setFileState: props.setFileState,
    setTableData: props.setTableData,
  });

  // ---------------------------------------------------------------------------
  // Back handler
  // ---------------------------------------------------------------------------

  async function onBack() {
    if (payment.isCartOrder) {
      payment.navigateTo(-1);
    } else if (isBulkOrder) {
      await bulkBack.fixIssues();
    } else {
      // Only restore the wizard's order total when a promo was applied here —
      // otherwise the parent's total is already correct and we'd zero it out.
      if (payment.isPromoApplied) finalPaymentAmount?.(payment.subTotalAmount);
      setActiveStep?.(3);
      setCallSubmitOrders?.(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Continue / Place order
  // ---------------------------------------------------------------------------

  async function onContinue() {
    payment.setSubmissionProcessing(true);
    if (
      payment.selectedCard?.cardExpiryStatus !== 'Expired' ||
      payment.isSkipCheckOut
    ) {
      if (payment.location?.state && payment.isCartOrder) {
        await placeCartOrder();
      } else {
        handleNonCartOrder();
      }
      payment.setSubmissionProcessing(false);
    } else {
      payment.dispatch(
        showAlert({ type: Variants.ERROR, message: 'Please select a valid card' })
      );
      payment.setSubmissionProcessing(false);
    }
  }

  async function placeCartOrder() {
    try {
      const data = payment.bulkOrderCartData as Record<string, unknown>;
      const orders = (data?.orders ?? []) as Array<Record<string, unknown>>;
      const withServices = orders.filter((o) => o.services);
      const withoutServices = orders.filter((o) => !o.services);

      const skip = payment.isSkipCheckOut;
      const free = skip || payment.totalAmount === 0;
      const card = payment.selectedCard;
      const bulkRequest = {
        ...data, orders: withServices, promocode: payment.promoCodeIdForOrderCart,
        cardNumber: free ? null : card?.cardNumber,
        cardType: free ? null : card?.cardType,
        nickName: free ? null : card?.nickName,
        payment: free ? null : buildOrderPayment(card),
      };

      const result = await payment.placeBulkOrder(bulkRequest).unwrap();
      if (result) {
        const responseData = updatesKeyOfBulkOrderCartData('items', result);
        const userId = localStorage.getItem('userId') ?? '';

        // Cart cleanup is best-effort — don't block confirmation navigation.
        // Asset lines are deleted by their stable lineItemUUID (DV-2131 §3) via the
        // dedicated endpoint, falling back to the (assetId + slug) composite for a
        // legacy line. Property lines use the generic bulk-delete.
        try {
          if (withoutServices.length > 0) {
            const assetLines = withServices.filter(
              (o) => typeof o.cartLineAssetId === 'string' && typeof o.cartLineSlug === 'string',
            );
            const propertyIds = withServices
              .filter((o) => typeof o.propertyUUID === 'string' && !(typeof o.cartLineAssetId === 'string' && typeof o.cartLineSlug === 'string'))
              .map((o) => o.propertyUUID as string);

            const assetDeletes = assetLines.map((o) =>
              payment.deleteAssetOrder({
                userId,
                // Prefer the stable lineItemUUID (DV-2131 §3); assetId + slug are the legacy
                // composite fallback for a line stored before the id existed.
                lineItemUUID: o.cartLineItemUUID as string | undefined,
                assetId: o.cartLineAssetId as string,
                slug: o.cartLineSlug as string,
              }).unwrap(),
            );
            const propertyDelete = propertyIds.length > 0
              ? [payment.deleteCartItems({ userId, lineItemUUIDs: propertyIds.join(',') }).unwrap()]
              : [];

            await Promise.all([...assetDeletes, ...propertyDelete]);
            payment.dispatch(setCartCount(withoutServices.length));
          } else if (withServices.length > 0) {
            await payment.deleteCart(userId).unwrap();
            payment.dispatch(setCartCount(0));
          }
        } catch { payment.dispatch(setCartCount(0)); }

        const { serviceImageMap, entityMap } = buildMapsFromLocation(payment.location);
        // Clear any stale Redux confirmation state from a previous order
        // before navigating. The cart confirmation page reads from the
        // location.state payload below (the bulk path uses the same
        // contract), so we don't need to also write to the Redux slice —
        // that would double-fire useOrderConfirmationData's effects with
        // the same data.
        payment.dispatch(clearOrderData());
        payment.navigateTo(ROUTES.ORDERS_CONFIRMATION, {}, {
          bulkOrderResponseData: responseData, serviceImageMap, entityMap, isCartOrder: true,
        });
      }
    } catch {
      payment.setSubmissionProcessing(false);
      payment.dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to place order' }));
    }
  }

  function handleNonCartOrder() {
    if (isBulkOrder) {
      setPromoCodeFromPaymentBulkOrder?.(payment.promoCodeId ?? null);
    } else {
      setPromoCodeFromPayment?.(payment.promoCodeId ?? null);
      setSavingsAmountFromPayment?.(payment.savingsAmount);
    }
    if ((isBulkOrder && Number(successData ?? 0) > 0) || isSingleOrderProp) {
      const cardInfo = !payment.isSkipCheckOut
        ? (payment.selectedCard ?? {})
        : undefined;

      // Direct callback — bypasses state→useEffect submit chain
      if (onSubmitSingleOrder && isSingleOrderProp) {
        onSubmitSingleOrder(
          cardInfo,
          payment.promoCodeId ?? null,
          payment.savingsAmount,
        );
      } else {
        // Bulk-import flow uses state-based chain (useBulkImportOrder useEffect)
        if (cardInfo) setPaymentInfo?.(cardInfo);
        setCallSubmitOrders?.(true);
      }
    } else {
      payment.dispatch(
        showAlert({ type: Variants.ERROR, message: 'There are no valid orders to make payment' })
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section
      className={
        payment.isCartOrder
          ? 'flex h-full flex-col bg-background'
          : 'absolute inset-0 flex flex-col bg-background'
      }
    >
      {payment.isCartOrder && (
        <div className="shrink-0 border-b border-border px-5 py-4">
          <Typography as="h1" variant="heading-2xl">
            Payment
          </Typography>
          <Typography as="p" variant="body" className="text-muted-foreground">
            Choose a payment method and complete your order
          </Typography>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div
          className={
            payment.isCartOrder ? 'p-6' : 'mx-auto max-w-6xl p-6'
          }
        >
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left: cards */}
          <div className="flex-1 overflow-hidden rounded-lg border border-border bg-background">
            <div className="flex items-center gap-3 border-b border-border px-6 py-4">
              <Wallet className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <Typography as="h2" variant="heading-md">
                Payment Method
              </Typography>
            </div>
            <div className="p-6">
            {payment.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                <SavedCardsList
                  cards={payment.cardsData}
                  selectedCard={payment.selectedCard}
                  isCardSelected={payment.isCardSelected}
                  onCardClick={payment.handleCardClick}
                  onUpdate={payment.openUpdateModal}
                  onDelete={payment.openDeleteModal}
                  onSetDefault={payment.handleSetDefault}
                  onAddCard={payment.addNewCard}
                  isSkipCheckOut={payment.isSkipCheckOut}
                  isMaxCardCount={payment.isMaxCardCount}
                  totalAmount={payment.totalAmount}
                  isBulkOrder={isBulkOrder}
                  fileNameInfo={fileNameInfo}
                  orderCount={orderCount}
                  failedCountInfo={failedCountInfo}
                />
                <CardFormModal
                  open={payment.showAddCardModal}
                  onOpenChange={payment.setShowAddCardModal}
                  isUpdate={false} isSingleCard={false}
                  onSuccess={payment.refetchCards}
                />
                <CardFormModal
                  open={payment.showUpdateCardModal}
                  onOpenChange={payment.setShowUpdateCardModal}
                  isUpdate isSingleCard={payment.isSingleCard}
                  cardDetails={payment.updateCardDetails}
                  onSuccess={payment.refetchCards}
                />
                <DeleteCardModal
                  open={payment.showDeleteCardModal}
                  onOpenChange={payment.setShowDeleteCardModal}
                  cardDetails={payment.deleteCardDetails}
                  selectedCard={payment.selectedCard}
                  setSelectedCard={payment.setSelectedCard}
                  onSuccess={payment.refetchCards}
                />
                <MaxCardModal
                  open={payment.showMaxCardModal}
                  onClose={() => payment.setShowMaxCardModal(false)}
                />
              </>
            )}
            </div>
          </div>
          {/* Right: order summary */}
          <div className="w-full shrink-0 lg:w-80">
            <PaymentOrderSummary
              subTotalAmount={payment.subTotalAmount}
              totalAmount={payment.totalAmount}
              savingsAmount={payment.savingsAmount}
              isPromoApplied={payment.isPromoApplied}
              appliedPromoDetails={payment.appliedPromoDetails}
              promoCode={payment.promoCode}
              onPromoCodeChange={payment.setPromoCode}
              onApplyPromo={payment.applyPromoCode}
              onRemovePromo={payment.removePromoCode}
              isPromocodeInvalid={payment.isPromocodeInvalid}
              promocodeMessage={payment.promocodeMessage}
              promoCodeLoading={payment.promoCodeLoading}
              promoInputResetKey={payment.promoInputResetKey}
            />
          </div>
        </div>
        </div>
      </div>
      {/* Footer */}
      <div className="shrink-0 flex items-center justify-between border-t border-border bg-background px-5 py-3.5">
        <Button
          type="button"
          variant="outline"
          className="min-w-[100px]"
          disabled={bulkBack.isBackLoading}
          onClick={onBack}
        >
          {bulkBack.isBackLoading && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Back
        </Button>
        <Button
          type="button"
          className="min-w-[120px]"
          disabled={
            payment.isSubmitDisabled() ||
            payment.submissionProcessing ||
            !!paymentLoader
          }
          onClick={onContinue}
        >
          {(payment.submissionProcessing || paymentLoader) && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Place order
        </Button>
      </div>
    </section>
  );
};

export default PaymentPage;
